import { Helmet } from "react-helmet-async";

import useI18n from "../i18n/useI18n";

const SITE_ORIGIN = "https://codeil.org";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/logo512.png`;

// Build an absolute canonical from a route path ("/", "/story", …).
const canonicalFor = (path = "/") => {
  const clean = `/${String(path).replace(/^\/+|\/+$/g, "")}`;
  return clean === "/" ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${clean}`;
};

const SEO = ({ title, description, path = "/", ogImage = DEFAULT_OG_IMAGE }) => {
  const { t, lang, dir } = useI18n();

  const resolvedTitle = title || t("seo.title");
  const resolvedDescription = description || t("seo.description");
  const canonicalUrl = canonicalFor(path);

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "CodeIL",
    url: `${SITE_ORIGIN}/`,
    description: resolvedDescription,
    knowsAbout: ["Software Development", "SaaS", "AI Integrations", "Web Development", ".NET", "React"],
    address: { "@type": "PostalAddress", addressCountry: "IL" },
    email: "info@codeil.org",
    telephone: "+972-50-2215233",
  };

  return (
    <Helmet htmlAttributes={{ lang, dir }}>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Hebrew is the default-served content at each URL; language toggles
          client-side, so self-reference he + x-default is the honest signal. */}
      <link rel="alternate" hrefLang="he" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      <meta property="og:site_name" content="CodeIL" />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={lang === "he" ? "he_IL" : "en_US"} />
      <meta property="og:locale:alternate" content={lang === "he" ? "en_US" : "he_IL"} />
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}

      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
    </Helmet>
  );
};

export default SEO;
